/**
 * BasicInfoForm - Step 1: Basic Project Information
 */

import React from 'react';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Typography,
  Paper,
  Grid,
  IconButton
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { BasicInfo, OrganisationInfo } from '../types';

interface Props {
  data: BasicInfo | null;
  onChange: (data: BasicInfo) => void;
}

const BasicInfoForm: React.FC<Props> = ({ data, onChange }) => {
  const [formData, setFormData] = React.useState<BasicInfo>(
    data || {
      title: '',
      acronym: '',
      program_type: 'KA220-ADU',
      duration: 24,
      budget: 250000,
      coordinator: {
        name: '',
        country: 'IT',
        type: 'University',
        role: 'Coordinator'
      },
      partners: [],
      // KA1 specific fields
      mobility_type: 'student',
      mobility_count: 10,
      destination_countries: []
    }
  );

  // Check if project is KA1 (mobility)
  const isKA1 = formData.program_type.startsWith('KA1');
  React.useEffect(() => {
    onChange(formData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const handleChange = (field: keyof BasicInfo, value: any) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleCoordinatorChange = (field: keyof OrganisationInfo, value: string) => {
    setFormData({
      ...formData,
      coordinator: { ...formData.coordinator, [field]: value }
    });
  };

  const addPartner = () => {
    setFormData({
      ...formData,
      partners: [
        ...formData.partners,
        {
          name: '',
          country: '',
          type: 'University',
          role: 'Partner'
        }
      ]
    });
  };

  const removePartner = (index: number) => {
    setFormData({
      ...formData,
      partners: formData.partners.filter((_, i) => i !== index)
    });
  };

  const updatePartner = (index: number, field: keyof OrganisationInfo, value: string) => {
    const newPartners = [...formData.partners];
    newPartners[index] = { ...newPartners[index], [field]: value };
    setFormData({ ...formData, partners: newPartners });
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom sx={{ color: 'white', fontWeight: 600 }}>
        📋 Basic Project Information
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: 'white !important' }}>
        Provide essential information about your Erasmus+ project
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          {/* Project Title */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Project Title"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="e.g., Enabling Digital Skills for Adults"
              required
              helperText="Full project title (10-200 characters)"
            />
          </Grid>

          {/* Acronym */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Project Acronym"
              value={formData.acronym}
              onChange={(e) => handleChange('acronym', e.target.value.toUpperCase())}
              placeholder="e.g., DigiSkills"
              required
              helperText="Short acronym (2-20 characters)"
            />
          </Grid>

          {/* Program Type */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Program Type</InputLabel>
              <Select
                value={formData.program_type}
                onChange={(e) => {
                  const newType = e.target.value;
                  handleChange('program_type', newType);
                  // Adjust defaults based on program type
                  if (newType.startsWith('KA1')) {
                    // KA1 defaults: shorter duration, lower budget
                    if (formData.duration > 24) handleChange('duration', 12);
                    if (formData.budget > 100000) handleChange('budget', 50000);
                  }
                }}
                label="Program Type"
              >
                {/* KA1 - Mobility Projects (Simpler) */}
                <MenuItem disabled sx={{ fontWeight: 700, color: '#003399', opacity: '1 !important' }}>
                  — KA1: Learning Mobility (Simpler) —
                </MenuItem>
                <MenuItem value="KA1-HED">KA1-HED - Higher Education Student Mobility</MenuItem>
                <MenuItem value="KA1-VET">KA1-VET - VET Learner & Staff Mobility</MenuItem>
                <MenuItem value="KA1-SCH">KA1-SCH - School Staff Mobility</MenuItem>
                <MenuItem value="KA1-ADU">KA1-ADU - Adult Education Staff Mobility</MenuItem>
                
                {/* KA2 - Partnership Projects (Complex) */}
                <MenuItem disabled sx={{ fontWeight: 700, color: '#003399', opacity: '1 !important', mt: 1 }}>
                  — KA2: Cooperation Partnerships (Complex) —
                </MenuItem>
                <MenuItem value="KA220-ADU">KA220-ADU - Adult Education Partnership</MenuItem>
                <MenuItem value="KA220-SCH">KA220-SCH - School Education Partnership</MenuItem>
                <MenuItem value="KA220-VET">KA220-VET - Vocational Training Partnership</MenuItem>
                <MenuItem value="KA220-HED">KA220-HED - Higher Education Partnership</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Duration */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Duration (months)"
              value={formData.duration}
              onChange={(e) => handleChange('duration', parseInt(e.target.value))}
              inputProps={{ min: isKA1 ? 6 : 12, max: isKA1 ? 24 : 36 }}
              required
              helperText={isKA1 ? "6-24 months" : "12-36 months"}
            />
          </Grid>

          {/* Budget */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Budget (EUR)"
              value={formData.budget}
              onChange={(e) => handleChange('budget', parseInt(e.target.value))}
              inputProps={{ 
                min: isKA1 ? 5000 : 60000, 
                max: isKA1 ? 150000 : 400000, 
                step: isKA1 ? 5000 : 10000 
              }}
              required
              helperText={isKA1 ? "€5,000 - €150,000 (based on unit costs)" : "€60,000 - €400,000"}
            />
          </Grid>

          {/* KA1 Specific Fields */}
          {isKA1 && (
            <>
              <Grid item xs={12}>
                <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255, 204, 0, 0.1)', borderRadius: 2, border: '1px solid #FFCC00' }}>
                  <Typography variant="subtitle2" sx={{ color: '#003399', fontWeight: 600, mb: 1 }}>
                    🎓 KA1 Mobility Project - Simpler Structure
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    KA1 projects focus on learning mobility for students and staff. No partner organisations required - 
                    you apply as a single organisation sending participants abroad.
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Mobility Type</InputLabel>
                  <Select
                    value={formData.mobility_type || 'student'}
                    onChange={(e) => handleChange('mobility_type', e.target.value)}
                    label="Mobility Type"
                  >
                    <MenuItem value="student">Students Only</MenuItem>
                    <MenuItem value="staff">Staff Only</MenuItem>
                    <MenuItem value="both">Students & Staff</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Number of Participants"
                  value={formData.mobility_count || 10}
                  onChange={(e) => handleChange('mobility_count', parseInt(e.target.value))}
                  inputProps={{ min: 1, max: 100 }}
                  required
                  helperText="Total participants to send abroad"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Destination Countries"
                  value={(formData.destination_countries || []).join(', ')}
                  onChange={(e) => handleChange('destination_countries', e.target.value.split(',').map(c => c.trim().toUpperCase()).filter(c => c))}
                  placeholder="DE, FR, ES"
                  helperText="Country codes separated by comma"
                />
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {/* Coordinator */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          👤 Coordinator Organisation
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Organisation Name"
              value={formData.coordinator.name}
              onChange={(e) => handleCoordinatorChange('name', e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Country"
              value={formData.coordinator.country}
              onChange={(e) => handleCoordinatorChange('country', e.target.value.toUpperCase())}
              placeholder="IT"
              required
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.coordinator.type}
                onChange={(e) => handleCoordinatorChange('type', e.target.value)}
                label="Type"
              >
                <MenuItem value="University">University</MenuItem>
                <MenuItem value="NGO">NGO</MenuItem>
                <MenuItem value="Company">Company</MenuItem>
                <MenuItem value="School">School</MenuItem>
                <MenuItem value="Training Center">Training Center</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Partners - Only for KA2 projects */}
      {!isKA1 && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              🤝 Partner Organisations
            </Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={addPartner}
              variant="outlined"
              size="small"
            >
              Add Partner
            </Button>
          </Box>

          {formData.partners.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              Click "Add Partner" to add partner organisations (minimum 2 partners recommended)
            </Typography>
          )}

          {formData.partners.map((partner, index) => (
            <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2">Partner {index + 1}</Typography>
                <IconButton onClick={() => removePartner(index)} size="small" color="error">
                  <DeleteIcon />
                </IconButton>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Organisation Name"
                    value={partner.name}
                    onChange={(e) => updatePartner(index, 'name', e.target.value)}
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Country"
                    value={partner.country}
                    onChange={(e) => updatePartner(index, 'country', e.target.value.toUpperCase())}
                  />
                </Grid>
                <Grid item xs={6} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={partner.type}
                      onChange={(e) => updatePartner(index, 'type', e.target.value)}
                      label="Type"
                    >
                      <MenuItem value="University">University</MenuItem>
                      <MenuItem value="NGO">NGO</MenuItem>
                      <MenuItem value="Company">Company</MenuItem>
                      <MenuItem value="School">School</MenuItem>
                      <MenuItem value="Training Center">Training Center</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>
          ))}
        </Paper>
      )}

      <Box sx={{ mt: 3, p: 2, bgcolor: isKA1 ? 'success.light' : 'info.light', borderRadius: 1 }}>
        <Typography variant="body2">
          {isKA1 ? (
            <>💡 <strong>KA1 Tip:</strong> Focus on clear learning outcomes and quality of hosting organisations. Budget is calculated based on unit costs per participant and destination country.</>
          ) : (
            <>💡 <strong>KA2 Tip:</strong> A strong consortium typically includes 3-4 partners from different countries with complementary expertise.</>
          )}
        </Typography>
      </Box>
    </Box>
  );
};

export default BasicInfoForm;

